import { useState } from 'react';
import { Review, ReviewStatus, ModerationStatus } from '@/types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatRelativeTime, getStatusColor, getModerationStatusColor, truncate } from '@/lib/utils';
import { Check, X, Eye, AlertTriangle, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ReviewDataTableProps {
  reviews: Review[];
  selectedReviews: string[];
  onSelectionChange: (reviewIds: string[]) => void;
  onApprove: (reviewId: string) => void;
  onReject: (reviewId: string) => void;
  onViewDetail: (reviewId: string) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
}

export function ReviewDataTable({
  reviews,
  selectedReviews,
  onSelectionChange,
  onApprove,
  onReject,
  onViewDetail,
  sortBy,
  sortOrder = 'desc',
  onSort,
}: ReviewDataTableProps) {
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(reviews.map((r) => r.reviewId));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (reviewId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedReviews, reviewId]);
    } else {
      onSelectionChange(selectedReviews.filter((id) => id !== reviewId));
    }
  };

  const handleSort = (column: string) => {
    if (!onSort) return;
    
    if (sortBy === column) {
      onSort(column, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(column, 'desc');
    }
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? (
      <ChevronUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline ml-1" />
    );
  };

  const allSelected = reviews.length > 0 && selectedReviews.length === reviews.length;
  const someSelected = selectedReviews.length > 0 && selectedReviews.length < reviews.length;

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox
                checked={allSelected}
                onCheckedChange={handleSelectAll}
                aria-label="Select all"
                className={cn(someSelected && 'data-[state=checked]:bg-primary/50')}
              />
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => handleSort('createdAt')}
            >
              Date <SortIcon column="createdAt" />
            </TableHead>
            <TableHead>SKU</TableHead>
            <TableHead>Product</TableHead>
            <TableHead>User</TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => handleSort('rating')}
            >
              Rating <SortIcon column="rating" />
            </TableHead>
            <TableHead>Content</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Moderation</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reviews.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                No reviews found
              </TableCell>
            </TableRow>
          ) : (
            reviews.map((review) => (
              <TableRow
                key={review.reviewId}
                className={cn(
                  review.moderationStatus === ModerationStatus.PreFlagged &&
                    'bg-orange-50 hover:bg-orange-100',
                  review.moderationStatus === ModerationStatus.Flagged &&
                    'bg-red-50 hover:bg-red-100',
                  selectedReviews.includes(review.reviewId) && 'bg-blue-50'
                )}
              >
                <TableCell>
                  <Checkbox
                    checked={selectedReviews.includes(review.reviewId)}
                    onCheckedChange={(checked) =>
                      handleSelectOne(review.reviewId, checked as boolean)
                    }
                    aria-label={`Select review ${review.reviewId}`}
                  />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatRelativeTime(review.createdAt)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground font-mono">
                  {review.productSku}
                </TableCell>
                <TableCell className="font-medium">
                  {truncate(review.productName, 30)}
                </TableCell>
                <TableCell>{review.userName}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className={i < review.rating ? 'text-yellow-400' : 'text-gray-300'}>
                        ★
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="max-w-xs">
                  <div className="text-sm">
                    <div className="font-medium">{truncate(review.title, 50)}</div>
                    <div className="text-muted-foreground">
                      {truncate(review.content, 80)}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={getStatusColor(review.status)}>
                    {review.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge className={getModerationStatusColor(review.moderationStatus)}>
                      {review.moderationStatus}
                    </Badge>
                    {review.flags.length > 0 && (
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {review.status === ReviewStatus.Pending && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onApprove(review.reviewId)}
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onReject(review.reviewId)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onViewDetail(review.reviewId)}
                      className="h-8 w-8 p-0"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
